import { toValue, type MaybeRefOrGetter } from 'vue'
import { PolicyType, SnpType } from '~/generated/api-clients-generated'
import type { QuotedPlanVm } from '~/models/quoting/QuotedPlan'

export default class SmartCardBadges {
  static async use(quote: MaybeRefOrGetter<QuotedPlanVm>, quoteIndex: MaybeRefOrGetter<number>) {
    const { getComponentContent } = Cms.use()
    const { content } = await getComponentContent('SmartCard')

    const headerBadge = computed(() => {
      const index = toValue(quoteIndex)
      if (index === 0) {
        return content.value.data.header_badges.top_plan
      }
      if (index > 0 && index < 3) {
        const badge = content.value.data.header_badges.top_3
        return {
          ...badge,
          text: badge.text.replace('{{index}}', index + 1)
        }
      }
      return null
    })

    const policies = computed((): Record<string, boolean> => {
      const { snpType, policyType } = toValue(quote)
      return {
        dsnp: snpType === SnpType.DualEligible,
        csnp: snpType === SnpType.ChronicCare,
        ppo: [PolicyType.PPO, PolicyType.LPPO, PolicyType.RPPO].includes(policyType),
        hmo: policyType === PolicyType.HMO,
        hmoPos: policyType === PolicyType.HMOPOS
      }
    })

    const applicablePolicies = computed(() =>
      Object.keys(policies.value).filter((x) => policies.value[x] === true)
    )

    const policyBadges = computed(() =>
      applicablePolicies.value.map((x) => {
        const { tag, variant } = content.value.data.policy_types?.[x] ?? {}

        return {
          tag,
          variant
        }
      })
    )

    const policyTable = computed(() =>
      applicablePolicies.value.map((x) => ({
        ...content.value.data.policy_types[x]?.callout,
        variant: content.value.data.policy_types[x]?.variant
      }))
    )

    const hasPartBGiveback = computed(() => toValue(quote).planAttributes.includes('partBGiveback'))

    return {
      content,
      headerBadge,
      applicablePolicies,
      policyBadges,
      policyTable,
      hasPartBGiveback
    }
  }
}
